import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc,
  serverTimestamp,
  limit,
  increment,
  getDoc,
  setDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { sendEmail } from '../../emailjs/emailService';

// Collection reference
const notificationsRef = collection(db, 'notifications');
const userNotificationCountsRef = collection(db, 'userNotificationCounts');

// Async thunks
export const fetchUserNotifications = createAsyncThunk(
  'notifications/fetchUserNotifications',
  async ({ email, markAsRead = false }, { rejectWithValue }) => {
    try {
      const notificationsQuery = query(
        notificationsRef,
        where('userEmail', '==', email),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      
      const snapshot = await getDocs(notificationsQuery);
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // If markAsRead is true, update unread notifications
      if (markAsRead) {
        const unreadNotifications = notifications.filter(notification => !notification.read);
        
        // Update each unread notification to be marked as read
        const updatePromises = unreadNotifications.map(notification => 
          updateDoc(doc(db, 'notifications', notification.id), { read: true })
        );
        
        if (updatePromises.length > 0) {
          await Promise.all(updatePromises);
          
          // Update user notification counter - use email as document ID
          const userCountDocRef = doc(userNotificationCountsRef, email);
          const userCountDoc = await getDoc(userCountDocRef);
          
          if (userCountDoc.exists()) {
            await updateDoc(userCountDocRef, { unreadCount: 0 });
          } else {
            await setDoc(userCountDocRef, { unreadCount: 0 });
          }
        }
      }
      
      return notifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return rejectWithValue(error.message || 'Failed to fetch notifications');
    }
  }
);

export const fetchNotificationCount = createAsyncThunk(
  'notifications/fetchNotificationCount',
  async (email, { rejectWithValue }) => {
    try {
      const userCountDocRef = doc(userNotificationCountsRef, email);
      const userCountDoc = await getDoc(userCountDocRef);
      
      if (userCountDoc.exists()) {
        return userCountDoc.data();
      } else {
        // If document doesn't exist, set default count
        await setDoc(userCountDocRef, { unreadCount: 0 });
        return { unreadCount: 0 };
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
      return rejectWithValue(error.message || 'Failed to fetch notification count');
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markNotificationAsRead',
  async ({ notificationId, userEmail }, { rejectWithValue }) => {
    try {
      const notificationDocRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationDocRef, { read: true });
      
      // Update the unread count in userNotificationCounts
      const userCountDocRef = doc(userNotificationCountsRef, userEmail);
      const userCountDoc = await getDoc(userCountDocRef);
      
      if (userCountDoc.exists() && userCountDoc.data().unreadCount > 0) {
        await updateDoc(userCountDocRef, {
          unreadCount: increment(-1)
        });
      }
      
      return notificationId;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return rejectWithValue(error.message || 'Failed to mark notification as read');
    }
  }
);

// Handle sending email for notification if email data is provided
const handleEmailNotification = async (emailData, notification) => {
  if (!emailData) return;
  
  try {
    // Generate a simple HTML template for the notification
    const mailBody = `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #f5f5f5; padding: 20px; border-radius: 8px;">
        <div style="background-color: #424242; padding: 20px; border-radius: 6px 6px 0 0;">
          <h2 style="color: #ffffff; margin: 0; font-weight: 500;">MyelinZ PPC Portal</h2>
        </div>
        <div style="background-color: #ffffff; padding: 30px; border-left: 1px solid #e0e0e0; border-right: 1px solid #e0e0e0;">
          <h3 style="color: #212121; margin-top: 0; font-size: 20px;">${notification.title}</h3>
          <p style="font-size: 15px; line-height: 1.6; color: #424242; margin-bottom: 25px;">${notification.message}</p>
          ${notification.actionLink ? `
            <div style="text-align: center; margin: 30px 0;">
              <a href="${notification.actionLink}" 
                 style="background-color: #424242; color: white; padding: 12px 24px; 
                        text-decoration: none; border-radius: 4px; display: inline-block;
                        font-weight: 500; transition: background-color 0.3s ease;">
                ${emailData.actionText || 'View Details'}
              </a>
            </div>
          ` : ''}
        </div>
        <div style="background-color: #212121; color: #f5f5f5; padding: 15px; text-align: center; font-size: 13px; border-radius: 0 0 6px 6px;">
          <p style="margin: 5px 0;">This is an automated message from the MyelinZ PPC Portal.</p>
          <p style="margin: 5px 0;">© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
        </div>
      </div>
    `;
    
    // Prepare email parameters
    const emailParams = {
      mailSubject: notification.title,
      mailBody,
      mailOne: emailData.to,
      firstName: emailData.firstName || ''
    };
    
    // Send the email using EmailJS
    const emailResult = await sendEmail(emailParams);
    
    if (emailResult.success) {
      console.log('Email notification sent successfully via EmailJS');
    } else {
      console.error('Failed to send email notification via EmailJS:', emailResult.error);
    }
  } catch (error) {
    console.error('Error sending email notification:', error);
  }
};

// Create a new notification
export const createNotification = createAsyncThunk(
  'notifications/createNotification',
  async ({ 
    userEmail, 
    title, 
    message, 
    type, 
    actionLink = '', 
    relatedDocId = '',
    createdBy = '',
    metadata = {},
    emailData // Optional data for email notification
  }, { dispatch, rejectWithValue }) => {
    try {
      // Add notification to Firestore
      const newNotification = {
        userEmail,
        title,
        message,
        type,
        actionLink: actionLink || '',
        relatedDocId: relatedDocId || '',
        read: false,
        createdAt: serverTimestamp(),
        createdBy,
        metadata,
        emailSent: false
      };
      
      const docRef = await addDoc(notificationsRef, newNotification);
      
      // Increment the user's unread notification count
      const userCountDocRef = doc(userNotificationCountsRef, userEmail);
      const userCountDoc = await getDoc(userCountDocRef);
      
      if (userCountDoc.exists()) {
        await updateDoc(userCountDocRef, {
          unreadCount: increment(1)
        });
      } else {
        await setDoc(userCountDocRef, { unreadCount: 1 });
      }
      
      // If emailData is provided, send an email notification
      if (emailData) {
        await handleEmailNotification(emailData, {
          title,
          message,
          type,
          actionLink
        });
      }
      
      // Update email status
      await updateDoc(doc(db, 'notifications', docRef.id), { 
        emailSent: true
      });
      
      return {
        id: docRef.id,
        ...newNotification,
        // Replace serverTimestamp with Date for immediate display
        createdAt: new Date(),
        emailSent: true
      };
    } catch (error) {
      console.error('Error creating notification:', error);
      return rejectWithValue(error.message || 'Failed to create notification');
    }
  }
);

// Helper function to determine if email should be sent (reserved for future use)
// eslint-disable-next-line no-unused-vars
function shouldSendEmail(notificationType) {
  const emailNotificationTypes = [
    'task_assigned',
    'task_updated',
    'leave_status_changed',
    'claim_status_changed',
    'performance_review',
    'document_shared'
  ];
  return emailNotificationTypes.includes(notificationType);
}

// Selector to get notifications for a specific user
export const selectUserNotifications = (state, email) => {
  if (!email || !state.notifications.notifications) return [];
  return state.notifications.notifications.filter(
    notification => notification.userEmail === email
  );
};

// Slice
const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
    createLoading: false,
    createError: null
  },
  reducers: {
    clearNotificationErrors: (state) => {
      state.error = null;
      state.createError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserNotifications
      .addCase(fetchUserNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch notifications';
      })
      
      // fetchNotificationCount
      .addCase(fetchNotificationCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unreadCount;
      })
      
      // markNotificationAsRead
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const index = state.notifications.findIndex(n => n.id === action.payload);
        if (index !== -1) {
          state.notifications[index].read = true;
        }
        if (state.unreadCount > 0) {
          state.unreadCount -= 1;
        }
      })
      
      // createNotification
      .addCase(createNotification.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.createLoading = false;
        // Only add to state if this notification is for current user
        // (This might be triggered for other users)
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload || 'Failed to create notification';
      });
  }
});

export const { clearNotificationErrors } = notificationSlice.actions;
export default notificationSlice.reducer; 